import { useAuth } from "@clerk/clerk-react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { runMigration } from "../services/supabase";
import { supabase } from "../utils/supabase";

// Create context
const TaskContext = createContext(null);

/**
 * TaskProvider - Centralized task data provider
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export function TaskProvider({ children }) {
  const { isSignedIn } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [migrationRun, setMigrationRun] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(Date.now());

  // Run migration once when the app loads
  useEffect(() => {
    const runDatabaseMigration = async () => {
      try {
        // Check if the task_attachments table exists
        const { error: checkError } = await supabase
          .from("task_attachments")
          .select("id")
          .limit(1);

        // Only run migration if the table doesn't exist
        if (checkError && checkError.code === "42P01") {
          console.log("🔄 Running database migration for task attachments...");
          await runMigration();
        }
      } catch (error) {
        console.error("Failed to run migration:", error);
        // Don't show error to user, just log it
      } finally {
        setMigrationRun(true);
      }
    };

    if (isSignedIn && !migrationRun) {
      runDatabaseMigration();
    }
  }, [isSignedIn, migrationRun]);

  // Function to fetch tasks from Supabase
  const fetchTasks = useCallback(async () => {
    if (!isSignedIn) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from("tasks")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        setError(error.message);
      } else {
        setTasks(data || []);
      }
    } catch (error) {
      setError("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, [isSignedIn]);

  // Fetch tasks when signed in or when refresh is triggered
  useEffect(() => {
    if (isSignedIn) {
      fetchTasks();
    }
  }, [isSignedIn, fetchTasks, refreshTrigger]);

  // Set up real-time subscription
  useEffect(() => {
    if (!isSignedIn) {
      setTasks([]);
      return;
    }

    // Set up real-time subscription
    const subscription = supabase
      .channel("public:tasks")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tasks" },
        (payload) => {
          console.log("🔄 Real-time update received:", payload);

          // Update tasks based on the event type
          if (payload.eventType === "INSERT") {
            setTasks((currentTasks) => [payload.new, ...currentTasks]);
          } else if (payload.eventType === "UPDATE") {
            setTasks((currentTasks) =>
              currentTasks.map((task) =>
                task.id === payload.new.id ? payload.new : task
              )
            );
          } else if (payload.eventType === "DELETE") {
            setTasks((currentTasks) =>
              currentTasks.filter((task) => task.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      // Clean up subscription when component unmounts
      supabase.removeChannel(subscription);
    };
  }, [isSignedIn]);

  // Force refresh of tasks
  const refreshTasks = useCallback(() => {
    setRefreshTrigger(Date.now());
  }, []);

  // Create a new task
  const createTask = useCallback(async (taskData) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("tasks")
        .insert(taskData)
        .select();

      if (error) {
        throw error;
      }

      // Immediately update local state
      if (data && data.length > 0) {
        setTasks((currentTasks) => [data[0], ...currentTasks]);
      }

      return data;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update a task
  const updateTask = useCallback(async (id, updates) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("tasks")
        .update(updates)
        .eq("id", id)
        .select();

      if (error) {
        throw error;
      }

      // Update local tasks state immediately
      if (data && data.length > 0) {
        setTasks((currentTasks) =>
          currentTasks.map((task) =>
            task.id === id ? { ...task, ...updates } : task
          )
        );
      }

      return data;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete a task
  const deleteTask = useCallback(async (id) => {
    try {
      setLoading(true);
      const { error } = await supabase.from("tasks").delete().eq("id", id);

      if (error) {
        throw error;
      }

      // Update local tasks state immediately
      setTasks((currentTasks) => currentTasks.filter((task) => task.id !== id));
      return true;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Value to be provided by context
  const value = {
    tasks,
    loading,
    error,
    createTask,
    updateTask,
    deleteTask,
    refreshTasks,
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

/**
 * useTaskContext - Custom hook to access task context
 */
export function useTaskContext() {
  const context = useContext(TaskContext);
  if (context === null) {
    throw new Error("useTaskContext must be used within a TaskProvider");
  }
  return context;
}

export default TaskProvider;
